<template>
  <div class="p-6 dark:text-gray-50" v-if="canAddTunnel || user()?.is_staff">
    <div class="flex justify-between items-center mb-6">
      <div class="flex space-x-3 items-center">
        <h1 class="text-2xl font-semibold dark:text-white">Add New Tunnel</h1>
        <div class="relative group">
        <button
          type="button"
           class="bg-transparent hover:bg-transparent"
          @click="startTutorial"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark dark:text-gray-50 text-gray-900"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
        </button>
          <span class="absolute top-full text-xs right-1/2 -translate-y-1/2 mb-1 px-2 py-0.5 glass-card opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">Tutorial</span>
        </div>
      </div>
      
      <button v-if="!isMultiSubmit"
        @click="goBack"
        class="btn bg-gray-300 text-gray-700 hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
      >
        Back to Tunnels
      </button>
    </div>

    <form @submit.prevent="handleSubmit" class="space-y-6 p-6 glass-card">
      <div class="">
        <h2 class="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-50">Tunnel Information {{ isMultiSubmit ? `- ${tunnelCount}` : '' }}</h2>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Number -->
          <div class="flex flex-col">
            <label for="tunnel_number" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Tunnel Number<span class="text-red-500">*</span>:</label>
            <input 
              v-model="formData.tunnel_number" 
              id="tunnel_number" 
              type="number"
              min="1"
              placeholder="Enter tunnel number"
              @focus="clearFieldError('tunnel_number')"
              :class="[ 'w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2', fieldErrors['tunnel_number']
                ? 'ring-2 ring-red-500  focus:ring-red-500'
                : 'focus:ring-blue-500'
              ]"
            />
            <FieldError :message="fieldErrors['tunnel_number']"/>
          </div>

          <!-- Site -->
          <div class="relative">
            <div class="relative mb-1">
            <label for="site" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Site<span class="text-red-500">*</span>:</label>
            </div>
            <input 
              v-model="siteSearch" 
              id="site" 
              placeholder="Search for site..."
              @focus="handleSiteFocus"
              @blur="handleSiteBlur"
              :disabled="isMultiSubmit" 
              :class="[ 'w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2', fieldErrors['site']
                ? 'ring-2 ring-red-500  focus:ring-red-500'
                : 'focus:ring-blue-500'
              ]"
            />
            <FieldError :message="fieldErrors['site']"/>
            <div
              v-show="isSiteDropdownVisible" 
              class="absolute w-full mt-1 max-h-60 overflow-y-auto bg-white dark:bg-gray-600 border border-gray-300 rounded shadow-lg z-10"
            >
              <div 
                v-for="site in filteredSites"
                :key="site.id"
                :value="site.id"
                class="px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                @mousedown="selectSite(site)"
              >
                {{ site.name }}
              </div>
              <div 
                v-if="filteredSites.length === 0" 
                class="px-3 py-2 text-gray-500"
              >
                No Sites found
              </div>
            </div>
          </div>

          <!-- Organization -->
          <div class="flex flex-col">
            <label for="org" class="mb-1 font-medium text-gray-700 dark:text-gray-50">
              Organisation<span class="text-red-500">*</span>:
            </label>
            <GlassSelect
              v-model="formData.org"
              id="org"
              :disabled="isMultiSubmit"
              @focus="clearFieldError('org')"
              :options="tenant.organizations.value"
              :searchable="true"
              placeholder="Select an organisation"
              :error="!!fieldErrors['org']"
            />
            <FieldError :message="fieldErrors['org']"/>
          </div>

          <!-- Address -->
          <div class="flex flex-col">
            <label for="address" class="mb-1 font-medium text-gray-700 dark:text-gray-50">IP Address<span class="text-red-500">*</span>:</label>
            <input 
              v-model="formData.address" 
              id="address" 
              type="text"
              placeholder="Enter IP address"
              @focus="clearFieldError('address')"
              :class="[ 'w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2', fieldErrors['address']
                ? 'ring-2 ring-red-500  focus:ring-red-500'
                : 'focus:ring-blue-500'
              ]"
            />
            <FieldError :message="fieldErrors['address']" 
            />
          </div>

          <!-- Port -->
          <div class="flex flex-col">
            <label for="port" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Port<span class="text-red-500">*</span>:</label>
            <input 
              v-model.number="formData.port" 
              id="port" 
              type="number"
              min="1"
              max="65535"
              placeholder="Enter port number"
              @focus="clearFieldError('port')"
              :class="[ 'w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2', fieldErrors['port']
                ? 'ring-2 ring-red-500  focus:ring-red-500'
                : 'focus:ring-blue-500'
              ]"
            />
            <FieldError :message="fieldErrors['port']"/>
          </div>

          <!-- Description -->
          <div class="flex flex-col md:col-span-2">
            <label for="description" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Description:</label>
            <textarea 
              v-model="formData.description" 
              id="description" 
              rows="3"
              placeholder="Enter tunnel description"
              class="border border-gray-300 dark:bg-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            ></textarea>
          </div>
        </div>

        <div class="mt-6 flex justify-end space-x-3">
          <button v-if="!isMultiSubmit"
            type="button"
            @click="goBack"
            class="btn bg-gray-300 text-gray-700 hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Cancel
          </button>
          <button v-if="!isMultiSubmit"
            id="submit-tunnel-btn"
            type="submit"
            class="btn bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
            Add Tunnel
          </button>
          <button v-if="isMultiSubmit"
            type="button"
            @click="handleSubmit"
            class="btn bg-green-600 text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring--500 disabled:opacity-50"
          >
            Save
          </button>
        </div>
      </div>
      <div v-show="displayNext" class="mt-6 flex justify-end space-x-3">
        <button v-if="isMultiSubmit"
        type="button"
        @click="AddMoreTunnels"
        class="btn bg-green-600 text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring--500 disabled:opacity-50"
        >
          Add More
        </button>
        <button v-if="isMultiSubmit"
          type="button"
          @click="configComplete"
          :disabled="loading"
          class="btn bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </form>
  </div>

  <!-- Error Message -->
  <PageError v-if="permsLoaded && !canAddTunnel && !user()?.is_staff"
    :error="errorMessage['403']"
    :errorCode="'403'" 
  />
</template>

<script>
import { ref, onMounted, computed, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { addTunnel } from "@/services/tunnelService";
import { getSites } from "@/services/siteService";
import FieldError from "@/components/FieldError.vue";
import GlassSelect from "@/components/GlassSelect.vue";
import { useStore } from "@/store";
import { errorMessages } from "@/utils/errorMessages";
import PageError from "@/components/PageError.vue";
import { useMultiTenant } from "@/composables/useMultiTenant";
import { useTutorial } from "@/composables/tutorial";

export default {
  name: "AddTunnelView",
  emits: ['tunnel-submitted'],
  props: {
    isMultiSubmit: {
      type: Boolean,
      default: false
    },
    multiSubmitOrg: {
      type: Number
    },
    newmultiSubmitSite: {
      type: Number
    },
    submittedSite: { 
      type: Object, 
      default: null 
    },
  },
  components: {
    FieldError,
    GlassSelect,
    PageError,
  },
  setup(props,{ emit }) {
    const selectingFromSite = ref(false);
    const tenant = useMultiTenant();
    const { start: _start } = useTutorial();
    const errorMessage = errorMessages;
    const fieldErrors = ref({});
    const router = useRouter();
    const loading = ref(false);
    const sites = ref([]);
    const formData = ref({
      tunnel_number: null,
      site: "",
      org: "",
      description: "",
      address: "",
      port: null,
    });
    const siteSearch = ref("");
    const isSiteDropdownVisible = ref(false);
    const multiSubmitSite = ref("");
    const tunnelCount = ref(1);
    const displayNext = ref(false);
    const canAddTunnel = ref(false);
    const permsLoaded = computed(() => useStore.permissionsLoadedRef.value);
    const route = useRoute(); 

    const user = () => {
     return useStore.getUser();
    };

    const clearFieldError = (fieldName) => {
      if (fieldErrors.value[fieldName]) {
        delete fieldErrors.value[fieldName];
        delete fieldErrors.value['non_field_errors'];
      }
    };

    const addTunnelTutorialSteps = [
      {
        element: '#tunnel_number',
        action: 'type',
        value: '1',
        message: 'Enter the tunnel number.'
      },
      {
        element: '#site',
        action: 'display',
        message: 'Search and select the site for this tunnel.'
      },
      {
        element: '#org',
        action: 'select',
        value: -1,
        message: 'Select the organisation this tunnel belongs to.'
      },
      {
        element: '#address',
        action: 'type',
        value: '192.168.1.1',
        message: 'Enter the IP address for this tunnel.'
      },
      {
        element: '#port',
        action: 'type',
        value: '8080',
        message: 'Enter the port number (1–65535).'
      },
      {
        element: '#description',
        action: 'type',
        value: 'Tutorial tunnel description',
        message: 'Optionally add a description for this tunnel.'
      },
      {
        element: '#submit-tunnel-btn',
        action: 'display',
        message: 'Click to create the tunnel.'
      },
    ];

    const startTutorial = async () => {
      const originalOrgs = [...tenant.organizations.value];
      const originalFormData = { ...formData.value };
      tenant.organizations.value = [{ id: -1, name: 'Tutorial Organisation' }];
      await _start(addTunnelTutorialSteps, () => {
        tenant.organizations.value = originalOrgs;
        formData.value = { ...originalFormData };
      });
    };

    const fetchSites = async (schemaName = null) => {
      try {

        // If in multi-submit mode, auto-select populate with submitted site and skip fetching all sites
        if (props.isMultiSubmit && props.submittedSite) {
          sites.value = [props.submittedSite];
          formData.value.org = props.submittedSite.org;
          selectSite(props.submittedSite);
          return;
        }
        sites.value = await getSites(schemaName);

      } catch (error) {
        console.error("Error fetching sites:", error);
      }
    };

    const handleSiteFocus = () => {
      clearFieldError('site')
      isSiteDropdownVisible.value = true;
      siteSearch.value = "";
    };

    const handleSiteBlur = () => {
      setTimeout(() => {
        isSiteDropdownVisible.value = false; 
      }, 200);
    };

    const selectSite = (site) => {
      formData.value.site = site.id;
      siteSearch.value = site.name;
      isSiteDropdownVisible.value = false;

      if (site.org) {
        selectingFromSite.value = true;
        formData.value.org = site.org;
      }
    };

    const filteredSites = computed(() => {
      if (!siteSearch.value) return sites.value;
      return sites.value.filter((site) =>
        site.name.toLowerCase().includes(siteSearch.value.toLowerCase()) ||
        site.id.toString().includes(siteSearch.value)
      );
    });

    const getSelectedSiteName = computed(() => {
      if (formData.value.site) return null;
      const selectedSite = sites.value.find(p => p.name === formData.value.site);
      return selectedSite? `${selectedSite.name} (ID: ${selectedSite.id})` : null;
    });

    const goBack = () => {
      router.push({ 
        name: 'Tunnels',
        query: tenant.getDetailPageParam()
      });
    };

    const AddMoreTunnels = () => {
      tunnelCount.value += 1;
      displayNext.value = false;
      formData.value = {
        tunnel_number: null,
        description: "",
        address: "",
        port: null,
        org: props.multiSubmitOrg
      };
      selectSite(multiSubmitSite.value);
    };

    const configComplete = () => {
      emit('tunnel-submitted', {
        tunnel_number: formData.value.tunnel_number,
        site: formData.value.site,
        org: formData.value.org,
        description: formData.value.description,
        address: formData.value.address,
        port: formData.value.port,
      });
    }


    const validateFields = (fields, fieldsToCheck) => {
      const errors = {};
      for (const fieldName of fieldsToCheck) {
        const value = fields[fieldName];
        if (!value || (typeof value === "string" && !value.trim()) || (value instanceof Array && value.length === 0)) {
          errors[fieldName] = "This field cannot be empty";
        }
      }
      return errors;
    };
     
    const handleSubmit = async () => {
      try {
        fieldErrors.value = {};
        loading.value = true;

        const validation = tenant.validateSchemaForOperation();
        if (!validation.valid) {
          error.value = "Malformed request. Failed to create tunnel!";
          errorCode.value = "422!";
          return;
        }

        fieldErrors.value = validateFields(formData.value, ['tunnel_number', 'site', 'address', 'port', 'org'])
        if (Object.keys(fieldErrors.value).length > 0) return;

        await addTunnel({
          tunnel_number: formData.value.tunnel_number,
          site: formData.value.site,
          org: formData.value.org,
          description: formData.value.description,
          address: formData.value.address,
          port: formData.value.port,
        }, validation.schemaName);

        if (!props.isMultiSubmit){
          const selectedOrg = tenant.organizations.value.find(org => org.id === formData.value.org);
          await tenant.switchOrganization(selectedOrg.id, () => {});
          router.push({ name: 'Tunnels' });
        }  else{
          displayNext.value = true;
        }
      } catch (error) {

        const errData = error?.response?.data;
        for (const element in  errData){
          const fieldError = errData?.[element];
          if (fieldError?.[0]) {
            fieldErrors.value[element] = fieldError[0]; 
          }
        }
        console.error("Error creating tunnel:", error);
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      tenant.fetchOrganizations();
      const validation = tenant.validateSchemaForOperation()
      fetchSites(validation.schemaName);
    });

    watch(
      () => useStore.getUser(),
      async (newUser) => {
        if (newUser) {
          canAddTunnel.value = await useStore.hasPermission("Can add tunnel");
        }
      },
      {immediate : true}
    );

    watch(
      () => formData.value.org,
      (orgId) => {
        if (!orgId) return;

        if (selectingFromSite.value) {
            selectingFromSite.value = false;
            return;
        }
        formData.value.site = "";
        siteSearch.value = "";

        const selectedOrg = tenant.organizations.value.find(org => org.id === orgId);
        if (!selectedOrg?.schema_name) return;

        const { schemaName, ...existingQuery } = route.query;
        router.replace({
          query: {
            ...existingQuery,
            client: selectedOrg.schema_name
          }
        });
        fetchSites(selectedOrg.schema_name);
      }
    );

    return {
      selectingFromSite,
      tenant,
      goBack,
      sites,
      formData,
      loading,
      handleSubmit,
      handleSiteBlur,
      handleSiteFocus,
      getSelectedSiteName,
      filteredSites,
      selectSite,
      siteSearch,
      isSiteDropdownVisible,
      FieldError,
      fieldErrors,
      AddMoreTunnels,
      configComplete,
      tunnelCount,
      displayNext,
      user,
      canAddTunnel,
      permsLoaded,
      errorMessage,
      startTutorial,
      clearFieldError
    };
  },
};
</script>
